import {
  ProjectRisk,
  ProjectRiskComment,
  ProjectRiskFeedback,
  RiskRanking,
  RiskStatus,
  RiskReviewSection,
} from '../../shared/interfaces/project/risk/risk-inteface'
import { buildQueryParams } from '../../utils/build-query-params'
import { apiSlice } from '../api-slice'
import {
  RiskList,
  CreateRiskList,
  UpdateRiskList,
  CustomRisk,
  CreateCustomRisk,
  UpdateCustomRisk,
  RunRiskListReviewArgs,
  RiskListPatch,
} from '../../shared/interfaces/project/risk/risk-list-interface'
import { InTextCitation } from '../../shared/interfaces/project/document/in-text-citation/in-text-citation.interface'

export interface ProjectRiskStats {
  count: number
  risk_ranking: RiskRanking
}

export interface ProjectRiskStatusStats {
  count: number
  status?: RiskStatus
}

export interface RiskPipelineStats {
  count: number
}

export interface GetProjectRisks {
  projectUUID: string
}

export interface ProjectRiskUpdate {
  id: string
  status?: RiskStatus
  riskRanking?: RiskRanking
  projectUUID: string
  riskStatusFilter?: RiskStatus
}

export interface GetRiskReviewSections {
  projectUUID: string
  sectionType: string
}

export interface RiskPreviewResult {
  id: string | null
  risk_name: string | null
  risk_description: string | null
  answer_text: string
  risk_ranking: number
  sources: InTextCitation[]
  risk_lists: any[]
  comments: any[]
  status: number
  processing_status: number
  risk_category: any | null
  default_position_met: boolean | null
  default_position_explanation: string | null
  default_position_explanation_sources: InTextCitation[]
  conflicts_text: string | null
  conflicts_sources: InTextCitation[]
  has_conflicts: boolean
  risk_acceptance_rate: number | null
  sources_count?: number
  relevant_contexts_count?: number
}

const projectRiskApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProjectRisk: builder.query<ProjectRisk[], GetProjectRisks>({
      query: ({ projectUUID }) => {
        const queryParams = buildQueryParams({
          project: projectUUID,
        })
        return `/projectrisks/?${queryParams.toString()}`
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'ProjectRisk' as const,
                id,
              })),
              { type: 'ProjectRisk' as const, id: 'LIST' },
            ]
          : [{ type: 'ProjectRisk' as const, id: 'LIST' }],
    }),
    getProjectRiskStats: builder.query<ProjectRiskStats[], string>({
      query: (projectUUID) => {
        const queryParams = buildQueryParams({
          project: projectUUID,
        })
        return `/projectrisks/stats/?${queryParams.toString()}`
      },
      transformResponse: (response: ProjectRiskStats[]) => {
        return response.map((projectRiskStat) => {
          return {
            ...projectRiskStat,
            risk_ranking: parseInt(projectRiskStat.risk_ranking.toString()),
          }
        }, [])
      },
    }),
    getRiskReviewSections: builder.query<
      RiskReviewSection[],
      GetRiskReviewSections
    >({
      query: ({ projectUUID, sectionType }) => {
        const queryParams = buildQueryParams({
          project_uuid: projectUUID,
          section_type: sectionType,
        })
        return `/riskreviewsections/?${queryParams.toString()}`
      },
      providesTags: (result, error, { projectUUID, sectionType }) => [
        {
          type: 'RiskReviewSection',
          id: `${projectUUID}-${sectionType}`,
        },
      ],
    }),
    getProjectRiskStatusStats: builder.query<ProjectRiskStatusStats[], string>({
      query: (projectUUID) => {
        const queryParams = buildQueryParams({
          project: projectUUID,
        })
        return `/projectrisks/status_stats/?${queryParams.toString()}`
      },
      transformResponse: (response: ProjectRiskStatusStats[]) => {
        return response.map((projectRiskStat) => {
          return {
            ...projectRiskStat,
            status: parseInt(projectRiskStat?.status?.toString() ?? ''),
          }
        }, [])
      },
    }),
    getProjectRisksProcessingStatus: builder.query<RiskPipelineStats, string>({
      query: (projectUUID) => {
        const queryParams = buildQueryParams({
          project: projectUUID,
        })
        return `/projectrisks/stats_analysing/?${queryParams.toString()}`
      },
    }),
    updateProjectRisk: builder.mutation<ProjectRisk, ProjectRiskUpdate>({
      query: ({ id, status, riskRanking, projectUUID }) => {
        return {
          url: `/projectrisks/${id}/`,
          method: 'PATCH',
          body: {
            status: status,
            risk_ranking: riskRanking,
          },
        }
      },
      async onQueryStarted(
        { id, status, riskRanking, projectUUID },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getProjectRisk',
            { projectUUID },
            (draft) => {
              const riskToUpdate = draft.find((risk) => risk.id === id)
              if (riskToUpdate) {
                if (status !== undefined) {
                  riskToUpdate.status = status
                }
                if (riskRanking !== undefined) {
                  riskToUpdate.risk_ranking = riskRanking
                }
              }
            }
          )
        )
        try {
          const { data } = await queryFulfilled
          // If the server returns updated data, we can update the cache with it
          dispatch(
            projectRiskApi.util.updateQueryData(
              'getProjectRisk',
              { projectUUID },
              (draft) => {
                const index = draft.findIndex((risk) => risk.id === id)
                if (index !== -1) {
                  draft[index] = data
                }
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
    }),
    createProjectRiskComment: builder.mutation<
      ProjectRiskComment,
      ProjectRiskComment
    >({
      query: (projectRiskComment) => ({
        url: '/projectriskcomments/',
        method: 'POST',
        body: projectRiskComment,
      }),
      async onQueryStarted(projectRiskComment, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getProjectRisk',
            projectRiskComment.project_uuid
              ? {
                  projectUUID: projectRiskComment.project_uuid,
                }
              : { projectUUID: '' },
            (draft) => {
              const projectRisk = projectRiskComment.parent
              if (!projectRisk) {
                return
              }
              const index = draft.findIndex(
                (projectRiskItr) => projectRiskItr.id === projectRisk.id
              )
              if (index !== -1) {
                const existingComments = draft[index].comments ?? []
                draft[index].comments = [
                  ...existingComments,
                  {
                    comment: projectRiskComment.comment,
                    date_created: new Date().toISOString(),
                    mentions: projectRiskComment.mentions,
                    user: projectRiskComment.user,
                  },
                ]
              }
            }
          )
        )
        try {
          const { data: result } = await queryFulfilled
          dispatch(
            projectRiskApi.util.updateQueryData(
              'getProjectRisk',
              projectRiskComment.project_uuid
                ? {
                    projectUUID: projectRiskComment.project_uuid,
                  }
                : { projectUUID: '' },
              (draft) => {
                const projectRisk = projectRiskComment.parent
                if (!projectRisk) {
                  return
                }
                const index = draft.findIndex(
                  (projectRiskItr) => projectRiskItr.id === projectRisk.id
                )
                if (index !== -1) {
                  const existingComments = draft[index].comments ?? []
                  draft[index].comments = [
                    ...existingComments.slice(0, -1),
                    result,
                  ]
                }
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
    }),
    submitProjectRiskFeedback: builder.mutation<
      ProjectRiskFeedback,
      ProjectRiskFeedback
    >({
      query: (projectRiskFeedback) => ({
        url: `/projectrisks/${projectRiskFeedback.project_risk}/feedback/`,
        method: 'POST',
        body: projectRiskFeedback,
      }),
    }),
    updateProjectRiskComment: builder.mutation<
      ProjectRiskComment,
      ProjectRiskComment
    >({
      query: (projectRiskComment) => ({
        url: `/projectriskcomments/${projectRiskComment.id}/`,
        method: 'PATCH',
        body: projectRiskComment,
      }),
      async onQueryStarted(projectRiskComment, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getProjectRisk',
            projectRiskComment.project_uuid
              ? {
                  projectUUID: projectRiskComment.project_uuid,
                }
              : { projectUUID: '' },
            (draft) => {
              const projectRisk = projectRiskComment.parent
              if (!projectRisk) {
                return
              }
              const index = draft.findIndex(
                (projectRiskItr) => projectRiskItr.id === projectRisk.id
              )
              if (index === -1) {
                return
              }
              const commentsIndex = draft[index]?.comments?.findIndex(
                (c) => c.id === projectRiskComment.id
              )
              const cleanIndex =
                commentsIndex === undefined ? -1 : commentsIndex
              if (cleanIndex === -1) {
                return
              }
              const comments = draft[index]?.comments
              if (!comments) {
                return
              }
              if (!comments[cleanIndex]) {
                return
              }
              comments[cleanIndex] = projectRiskComment
            }
          )
        )
        try {
          const { data: result } = await queryFulfilled
          dispatch(
            projectRiskApi.util.updateQueryData(
              'getProjectRisk',
              projectRiskComment.project_uuid
                ? {
                    projectUUID: projectRiskComment.project_uuid,
                  }
                : { projectUUID: '' },
              (draft) => {
                const projectRisk = projectRiskComment.parent
                if (!projectRisk) {
                  return
                }
                const index = draft.findIndex(
                  (projectRiskItr) => projectRiskItr.id === projectRisk.id
                )
                if (index !== -1) {
                  const existingComments = draft[index].comments ?? []
                  draft[index].comments = [
                    ...existingComments.slice(0, -1),
                    result,
                  ]
                }
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: [{ type: 'ProjectRisk', id: 'LIST' }],
    }),
    deleteProjectRiskComment: builder.mutation<
      ProjectRiskComment,
      ProjectRiskComment
    >({
      query: ({ id }) => ({
        url: `/projectriskcomments/${id}/`,
        method: 'DELETE',
      }),
      // optimistic update
      async onQueryStarted(projectRiskComment, { dispatch, queryFulfilled }) {
        const deleteResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getProjectRisk',
            projectRiskComment.project_uuid
              ? {
                  projectUUID: projectRiskComment.project_uuid,
                }
              : { projectUUID: '' },
            (draft) => {
              const index = draft.findIndex(
                (projectRisk) =>
                  projectRisk.id === projectRiskComment.project_risk_id
              )
              const commentsIndex =
                projectRiskComment?.parent?.comments?.findIndex(
                  (rc) => rc?.id === projectRiskComment?.id
                )
              const cleanIndex =
                commentsIndex === undefined ? -1 : commentsIndex
              if (cleanIndex === -1) {
                return
              }
              if (cleanIndex !== -1 && projectRiskComment?.parent?.comments) {
                if ((draft[index]?.comments?.length ?? 0) > 1) {
                  draft[index]?.comments?.splice(cleanIndex, 1)
                } else {
                  draft[index].comments = []
                }
              }
              draft[index] = { ...draft[index] }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          deleteResult.undo()
        }
      },
      invalidatesTags: (result, error, projectRiskComment) => [
        {
          type: 'ProjectRisk',
          id: projectRiskComment.project_uuid,
          status: projectRiskComment.status,
        },
      ],
    }),
    createRiskList: builder.mutation<RiskList, CreateRiskList>({
      query: ({ name }) => ({
        url: `/risklists/`,
        method: 'POST',
        body: { name },
      }),
      invalidatesTags: [{ type: 'RiskLists', id: 'LIST' }],
      async onQueryStarted({ name }, { dispatch, queryFulfilled }) {
        const tempId = Date.now()
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getRiskLists',
            false,
            (draft: RiskListPatch[]) => {
              draft.push({
                id: tempId,
                name,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              })
            }
          )
        )
        try {
          const { data } = await queryFulfilled
          dispatch(
            projectRiskApi.util.updateQueryData(
              'getRiskLists',
              false,
              (draft) => {
                const index = draft.findIndex((list) => list.id === tempId)
                if (index !== -1) draft[index] = data
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
    }),

    updateRiskList: builder.mutation<RiskList, UpdateRiskList>({
      query: ({ id, name }) => ({
        url: `/risklists/${id}/`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'RiskLists', id }],
      async onQueryStarted({ id, name }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getRiskLists',
            false,
            (draft) => {
              const riskList = draft.find((list) => list.id === id)
              if (riskList) {
                riskList.name = name
                riskList.updated_at = new Date().toISOString()
              }
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    deleteRiskList: builder.mutation<void, number>({
      query: (id) => ({
        url: `/risklists/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'RiskLists', id }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getRiskLists',
            false,
            (draft) => {
              const index = draft.findIndex((list) => list.id === id)
              if (index !== -1) draft.splice(index, 1)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    getRiskLists: builder.query<RiskList[], boolean>({
      query: (isAll = false) => `/risklists/?all=${isAll}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'RiskLists' as const, id })),
              { type: 'RiskLists', id: 'LIST' },
            ]
          : [{ type: 'RiskLists', id: 'LIST' }],
    }),

    getRiskListFilters: builder.query<RiskList[], number>({
      query: (projectId) => `/risklistfilter/?project=${projectId}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'RiskLists' as const, id })),
              { type: 'RiskListFilters', id: 'LIST' },
            ]
          : [{ type: 'RiskListFilters', id: 'LIST' }],
    }),

    getRiskListById: builder.query<RiskList, number>({
      query: (id) => `/risklists/${id}/`,
      providesTags: (result, error, id) => [{ type: 'RiskLists', id }],
    }),

    getRiskPipelineDefinitions: builder.query<CustomRisk[], string>({
      query: (risk_list_id) =>
        `/riskpipelinedefinitions/?risk_list_template_id=${risk_list_id}`,
      transformResponse: (response: any[]): CustomRisk[] =>
        response.map(
          ({
            id,
            risk_name,
            query_text,
            risk_description,
            default_position_prompt_template,
          }) => ({
            id,
            risk_name,
            query_text,
            risk_description,
            default_position_prompt_template,
          })
        ),
      providesTags: (result, error, risk_list_id) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Risks' as const, id })),
              { type: 'Risks', id: 'LIST' },
              { type: 'Risks', id: risk_list_id },
            ]
          : [
              { type: 'Risks', id: 'LIST' },
              { type: 'Risks', id: risk_list_id },
            ],
    }),

    createRiskPipelineDefinition: builder.mutation<
      CustomRisk,
      CreateCustomRisk
    >({
      query: ({
        risk_name,
        query_text,
        risk_description,
        risk_list_template_id,
        default_position_prompt_template,
      }) => ({
        url: `/riskpipelinedefinitions/`,
        method: 'POST',
        body: {
          risk_name,
          query_text,
          risk_description,
          risk_list_template_id,
          ...(default_position_prompt_template && {
            default_position_prompt_template,
          }),
        },
      }),
      invalidatesTags: (result, error, { risk_list_template_id }) => [
        { type: 'Risks', id: 'LIST' },
        { type: 'Risks', id: risk_list_template_id },
      ],
      async onQueryStarted(
        {
          risk_name,
          query_text,
          risk_description,
          risk_list_template_id,
          default_position_prompt_template,
        },
        { dispatch, queryFulfilled }
      ) {
        const tempId = Date.now().toString()
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getRiskPipelineDefinitions',
            risk_list_template_id,
            (draft) => {
              draft.unshift({
                id: tempId,
                risk_name,
                query_text,
                risk_description,
                default_position_prompt_template,
              })
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    updateRiskPipelineDefinition: builder.mutation<
      CustomRisk,
      UpdateCustomRisk
    >({
      query: ({ id, default_position_prompt_template, ...updateFields }) => ({
        url: `/riskpipelinedefinitions/${id}/`,
        method: 'PATCH',
        body: {
          ...Object.fromEntries(
            Object.entries(updateFields).filter(
              ([_, value]) => value !== undefined
            )
          ),
          ...(default_position_prompt_template && {
            default_position_prompt_template,
          }),
        },
      }),
      invalidatesTags: (result, error, { id, risk_list_template_id }) => [
        { type: 'Risks', id },
        { type: 'Risks', id: risk_list_template_id },
      ],
      async onQueryStarted(
        { id, risk_name, query_text, risk_list_template_id },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getRiskPipelineDefinitions',
            risk_list_template_id,
            (draft) => {
              const riskToUpdate = draft.find((risk) => risk.id === id)
              if (riskToUpdate) {
                if (risk_name) {
                  riskToUpdate.risk_name = risk_name
                }
                if (query_text) {
                  riskToUpdate.query_text = query_text
                }
              }
            }
          )
        )
        try {
          const { data } = await queryFulfilled
          dispatch(
            projectRiskApi.util.updateQueryData(
              'getRiskPipelineDefinitions',
              risk_list_template_id,
              (draft) => {
                const index = draft.findIndex((risk) => risk.id === id)
                if (index !== -1) draft[index] = data
              }
            )
          )
        } catch {
          patchResult.undo()
        }
      },
    }),

    deleteRiskPipelineDefinition: builder.mutation<
      void,
      { id: string; risk_list_template_id: string }
    >({
      query: ({ id }) => ({
        url: `/riskpipelinedefinitions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id, risk_list_template_id }) => [
        { type: 'Risks', id },
        { type: 'Risks', id: risk_list_template_id },
      ],
      async onQueryStarted(
        { id, risk_list_template_id },
        { dispatch, queryFulfilled }
      ) {
        const patchResult = dispatch(
          projectRiskApi.util.updateQueryData(
            'getRiskPipelineDefinitions',
            risk_list_template_id,
            (draft) => {
              const index = draft.findIndex((risk) => risk.id === id)
              if (index !== -1) draft.splice(index, 1)
            }
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
    }),

    runRiskListReview: builder.mutation<void, RunRiskListReviewArgs>({
      query: (body) => ({
        url: '/risklists/run_review/',
        method: 'POST',
        body,
      }),
    }),
    generateRiskRFI: builder.mutation<{ rfi_text: string }, string>({
      query: (project_risk_id) => ({
        url: `/projectrisks/${project_risk_id}/generate_rfi/`,
        method: 'POST',
      }),
    }),
    getRiskListShares: builder.query<
      {
        shared_users: { email: string; permission: 'view' | 'edit' }[]
        invited_users: string[]
        created_by: string
      },
      string | number
    >({
      query: (id) => `/risklists/${id}/share/`,
    }),
    shareRiskListTemplate: builder.mutation<
      void,
      {
        id: string | number
        payload: { users: { email: string; permission: string }[] }
      }
    >({
      query: ({ id, payload }) => ({
        url: `/risklists/${id}/share/`,
        method: 'POST',
        body: payload,
      }),
    }),
    unshareRiskListTemplate: builder.mutation<
      { unshared_users: string[] },
      { id: string | number; payload: { users: string[] } }
    >({
      query: ({ id, payload }) => ({
        url: `/risklists/${id}/unshare/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ProjectRisk', id: 'LIST' }],
    }),
    previewRisk: builder.mutation<
      RiskPreviewResult,
      {
        project_uuid: string
        query_text: string
        default_position_prompt_template?: string
        prompt_template?: string
        ranking_prompt_template?: string
        language?: string
      }
    >({
      query: (body) => ({
        url: '/riskpipelinedefinitions/preview-risk-review/',
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  useGetProjectRiskQuery,
  useGetProjectRiskStatsQuery,
  useGetProjectRisksProcessingStatusQuery,
  useCreateProjectRiskCommentMutation,
  useUpdateProjectRiskCommentMutation,
  useDeleteProjectRiskCommentMutation,
  useUpdateProjectRiskMutation,
  useSubmitProjectRiskFeedbackMutation,
  useGetProjectRiskStatusStatsQuery,
  useCreateRiskListMutation,
  useUpdateRiskListMutation,
  useDeleteRiskListMutation,
  useGetRiskListsQuery,
  useGetRiskListByIdQuery,
  useCreateRiskPipelineDefinitionMutation,
  useUpdateRiskPipelineDefinitionMutation,
  useGetRiskPipelineDefinitionsQuery,
  useDeleteRiskPipelineDefinitionMutation,
  useRunRiskListReviewMutation,
  useGetRiskListFiltersQuery,
  useGenerateRiskRFIMutation,
  useGetRiskListSharesQuery,
  useShareRiskListTemplateMutation,
  useUnshareRiskListTemplateMutation,
  useGetRiskReviewSectionsQuery,
  usePreviewRiskMutation,
} = projectRiskApi
